(() => {
  if (window.AplovioLayers) return;
  const stack = [], bindings = new WeakMap(), movements = new WeakMap(), contents = new WeakMap(), views = new WeakMap();
  const awaitingHistory = new WeakSet(), reopenRequests = new Map();
  const historySession = crypto.randomUUID(), historyDialogs = new Map();
  let historySync = false, knownHistoryDepth = 0;
  const ownsHistory = dialog => dialog.hasAttribute('data-layer-history') && !dialog.closest('#AplovioProductLayer') && !window.Shopify?.designMode;
  let lock, scrollRestoration;
  const top = () => stack.at(-1)?.dialog;
  const focusControl = dialog => [...(dialog?.querySelectorAll('[autofocus], [data-layer-close]') || [])].find(node => node.getClientRects().length && !node.disabled);
  const emit = (dialog, name, detail, cancelable = false) => dialog.dispatchEvent(new CustomEvent(`aplovio:layer-${name}`, {bubbles:true, cancelable, detail}));
  const coordinated = dialog => dialog.dataset.layerMotion === 'coordinated';
  const closing = dialog => movements.get(dialog)?.kind === 'closing';
  const disclosures = new Map();
  function motionDuration(node,kind) {
    if(matchMedia('(prefers-reduced-motion: reduce)').matches)return 0;
    const value=getComputedStyle(node).getPropertyValue(`--ap-layer-${kind}-motion`).trim();
    return Math.min(1600,Math.max(0,parseFloat(value)*(value.endsWith('ms')?1:1000)))||0;
  }
  function cancelDisclosure(details) {
    const entry=disclosures.get(details);disclosures.delete(details);
    entry?.animation.cancel();if(entry)entry.body.inert=false;
    details.removeAttribute('data-disclosure-phase');
  }
  function disclose(details,body,expanded,{animate=true,onFinish}={}) {
    if(!details || !body)return null;
    const active=disclosures.get(details),style=getComputedStyle(body);
    const from={height:`${details.open?body.getBoundingClientRect().height:0}px`,opacity:active?style.opacity:String(details.open?1:0),paddingBlockStart:details.open?style.paddingBlockStart:'0px',paddingBlockEnd:details.open?style.paddingBlockEnd:'0px'};
    cancelDisclosure(details);
    const trigger=details.querySelector('summary');trigger?.setAttribute('aria-expanded',String(expanded));
    if(!expanded && body.contains(document.activeElement))trigger?.focus({preventScroll:true});
    const finish=()=>{details.open=expanded;cancelDisclosure(details);onFinish?.();};
    const ms=animate?motionDuration(details,'content')*(expanded?1:.8):0;
    if(!ms){finish();return null;}
    details.open=true;
    const natural=getComputedStyle(body),to={height:`${expanded?body.getBoundingClientRect().height:0}px`,opacity:expanded?1:0,paddingBlockStart:expanded?natural.paddingBlockStart:'0px',paddingBlockEnd:expanded?natural.paddingBlockEnd:'0px'};
    details.dataset.disclosurePhase=expanded?'opening':'closing';body.inert=!expanded;
    const animation=body.animate([from,to],{duration:ms,easing:getComputedStyle(details).getPropertyValue('--ap-ease').trim()||'ease-out',fill:'both'});
    disclosures.set(details,{animation,body,expanded,finish});
    animation.finished.then(()=>{if(disclosures.get(details)?.animation===animation)finish();}).catch(()=>{});
    return animation;
  }
  document.addEventListener('click',event=>{
    if(event.defaultPrevented)return;
    const summary=event.target.closest('summary'),details=summary?.parentElement;
    if(!details?.matches('.ap-cart-disclosure,.ap-search-filters,.ap-filter-group,.ap-purchase-disclosure'))return;
    const body=details.querySelector(':scope > :not(summary)');if(!body)return;
    event.preventDefault();disclose(details,body,!(disclosures.get(details)?.expanded??details.open));
  });
  matchMedia('(prefers-reduced-motion:reduce)').addEventListener('change',event=>{if(event.matches)for(const entry of [...disclosures.values()])entry.finish();});
  document.addEventListener('shopify:section:unload',event=>{for(const [details,entry] of disclosures)if(event.target.contains(details))entry.finish();});
  function duration(dialog,kind) {
    return coordinated(dialog)?motionDuration(dialog,kind):0;
  }
  function cancelMovement(dialog) {
    const movement = movements.get(dialog);
    movements.delete(dialog); movement?.animation.cancel();
    dialog.removeAttribute('data-layer-phase');
    dialog.style.removeProperty('transform');
    dialog.style.removeProperty('--ap-layer-drag-backdrop');
  }
  function animateContent(dialog,node,{direction} = {}) {
    contents.get(dialog)?.cancel();contents.delete(dialog);
    const ms=duration(dialog,'content');
    if(ms && !node.hidden){
      const style=getComputedStyle(dialog);
      const sign=(direction==='back'?-1:1)*(style.direction==='rtl'?-1:1);
      const from=direction ? `translateX(${sign*100}%)` : `translateY(${style.getPropertyValue('--ap-space-1').trim() || '0px'})`;
      const animation=node.animate([{opacity:direction?1:.65,transform:from},{opacity:1,transform:'translate(0,0)'}],{duration:ms,easing:style.getPropertyValue('--ap-ease').trim() || 'ease-out'});
      contents.set(dialog,animation);
    }
  }
  function offset(dialog) {
    const side = getComputedStyle(dialog).getPropertyValue('--ap-layer-presentation').trim();
    return side === 'bottom' ? 'translateY(100%)' : side === 'left' ? 'translateX(-100%)' : side === 'right' ? 'translateX(100%)' : `translateY(${getComputedStyle(dialog).getPropertyValue('--ap-layer-enter-y').trim() || '0px'})`;
  }
  function animate(dialog,kind,from,done) {
    const backdrop = parseFloat(getComputedStyle(dialog,'::backdrop').opacity);
    cancelMovement(dialog);
    const ms = duration(dialog,kind === 'opening' ? 'open' : 'close');
    if (!ms) {done?.(); return;}
    dialog.style.setProperty('--ap-layer-backdrop-start',String(kind === 'opening' && !from ? 0 : Number.isFinite(backdrop) ? backdrop : 1));
    dialog.dataset.layerPhase = kind;
    // Page navigation moves an opaque screen; drawers retain the shared surface fade.
    const hiddenOpacity = dialog.dataset.presentation === 'page' ? 1 : 0;
    const end = kind === 'closing' ? {transform:offset(dialog),opacity:hiddenOpacity} : {transform:'translate(0,0)',opacity:1};
    const animation = dialog.animate([from || {transform:offset(dialog),opacity:hiddenOpacity},end],{duration:ms,easing:getComputedStyle(dialog).getPropertyValue('--ap-layer-ease').trim() || 'ease-out',fill:'both'});
    const movement = {animation,kind,done}; movements.set(dialog,movement);
    animation.finished.then(() => {
      if (movements.get(dialog) !== movement) return;
      cancelMovement(dialog); done?.();
    }).catch(() => {});
  }
  function release(dialog, reason = 'close') {
    const index = stack.findIndex(entry => entry.dialog === dialog);
    if (index < 0) return;
    const [entry] = stack.splice(index, 1);
    contents.get(dialog)?.cancel();contents.delete(dialog);
    cancelMovement(dialog); awaitingHistory.delete(dialog);
    if (!stack.length && lock) {
      for (const [property, value] of Object.entries(lock.styles)) document.body.style[property] = value;
      window.scrollTo({left:lock.x, top:lock.y, behavior:'instant'});
      lock = null;
      const restoration = scrollRestoration;
      restoration.frame = requestAnimationFrame(() => {
        if (!stack.length && scrollRestoration === restoration) {
          history.scrollRestoration = restoration.mode;
          scrollRestoration = null;
        }
      });
    }
    const target = entry.trigger?.isConnected && !entry.trigger.closest('[inert]') ? entry.trigger : focusControl(top()) || document.querySelector('main, [data-ap-main]');
    target?.focus({preventScroll:true});
    emit(dialog, 'close', {reason});
  }
  function close(dialog = top(), reason = 'close', force = false) {
    if (!dialog || (!force && dialog !== top())) return false;
    if (reason !== 'disconnect' && (closing(dialog) || awaitingHistory.has(dialog))) return true;
    if (!force && !emit(dialog, 'beforeclose', {reason}, true)) return false;
    const position = stack.findIndex(entry => entry.dialog === dialog);
    if (position >= 0) [...stack.slice(position + 1)].reverse().forEach(entry => close(entry.dialog, reason, true));
    const finish = () => {dialog.close(); release(dialog, reason);};
    contents.get(dialog)?.cancel();contents.delete(dialog);
    if (coordinated(dialog) && dialog.open && reason !== 'disconnect') {
      const style = getComputedStyle(dialog);
      animate(dialog,'closing',{transform:style.transform,opacity:style.opacity},finish);
    } else {cancelMovement(dialog); finish();}
    return true;
  }
  function manage(dialog) {
    if (bindings.has(dialog)) return;
    const abort = new AbortController(), options = {signal:abort.signal};
    bindings.set(dialog, abort);
    matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change',event=>{if(event.matches){contents.get(dialog)?.cancel();contents.delete(dialog);const movement=movements.get(dialog);if(movement){cancelMovement(dialog);movement.done?.();}}},options);
    dialog.addEventListener('cancel', event => {event.preventDefault(); close(dialog, 'escape');}, options);
    dialog.addEventListener('close', () => {if (!dialog.open) release(dialog);}, options);
    let backdropStart = false, drag, contentDrag, suppressClickUntil = 0;
    const paintDrag = (distance, side) => {
      dialog.style.transform = side === 'bottom' ? `translateY(${distance}px)` : `translateX(${distance * (side === 'left' ? -1 : 1)}px)`;
      dialog.style.setProperty('--ap-layer-drag-backdrop', String(Math.max(0, 1 - distance / (side === 'bottom' ? dialog.clientHeight : dialog.clientWidth))));
    };
    const resetDrag = () => {
      const previous=drag;drag = null; backdropStart = false;
      contentDrag = null;
      if (previous?.handle.hasPointerCapture(previous.id)) previous.handle.releasePointerCapture(previous.id);
    };
    const snapBack = () => {
      if (coordinated(dialog) && dialog.dataset.layerPhase === 'dragging') {
        const style = getComputedStyle(dialog);
        animate(dialog,'settling',{transform:style.transform,opacity:1});
      }
      resetDrag();
    };
    dialog.addEventListener('pointerdown', event => {
      backdropStart = event.target === dialog;
      const handle = event.target.closest('[data-layer-handle]');
      if (!handle || event.target.closest('button,a,input,select,textarea') || top() !== dialog || closing(dialog) || event.button !== 0 || event.isPrimary === false) return;
      const side = getComputedStyle(dialog).getPropertyValue('--ap-layer-presentation').trim();
      if (!['left','right','bottom'].includes(side)) return;
      drag = {x:event.clientX, y:event.clientY, id:event.pointerId, side,handle,distance:0,perpendicular:0};
      if (coordinated(dialog)) {cancelMovement(dialog);dialog.dataset.layerPhase = 'dragging';}
      handle.setPointerCapture(event.pointerId);
    }, options);
    dialog.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId || !coordinated(dialog)) return;
      if(closing(dialog)){resetDrag();return;}
      const dx = event.clientX - drag.x,dy = event.clientY - drag.y;
      drag.distance = Math.max(0,drag.side === 'bottom' ? dy : dx * (drag.side === 'left' ? -1 : 1));
      drag.perpendicular = drag.side === 'bottom' ? Math.abs(dx) : Math.abs(dy);
      if (drag.perpendicular > 12 && drag.perpendicular > drag.distance * 1.5) {snapBack();return;}
      paintDrag(drag.distance, drag.side);
    }, options);
    dialog.addEventListener('pointerup', event => {
      if (drag?.id === event.pointerId) {
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        const distance = drag.side === 'bottom' ? dy : dx * (drag.side === 'left' ? -1 : 1);
        const perpendicular = drag.side === 'bottom' ? Math.abs(dx) : Math.abs(dy);
        const threshold = drag.side === 'bottom' ? dialog.clientHeight / 2 : 64;
        if (distance > threshold && distance > perpendicular * 1.5) {resetDrag();close(dialog, 'gesture');}
        else snapBack();
      } else if (backdropStart && event.target === dialog && dialog.dataset.backdrop !== 'false') {
        const surfaces = [...dialog.querySelectorAll('[data-layer-surface]')].filter(surface => surface.getClientRects().length);
        const outside = (surfaces.length ? surfaces : [dialog]).every(surface => {const bounds = surface.getBoundingClientRect(); return event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;});
        if (outside) close(dialog, 'backdrop');
      }
      if (!contentDrag) resetDrag();
    }, options);
    dialog.addEventListener('pointercancel', () => {if (drag) snapBack();}, options);
    dialog.addEventListener('lostpointercapture', () => {if(drag) snapBack();}, options);
    // Touch content can hand off a downward pan to its sheet at scrollTop=0.
    // Keep native vertical scrolling and horizontal card gestures until the
    // downward intent is clear; touch-action:none on the body would break both.
    dialog.addEventListener('touchstart', event => {
      if (event.touches.length !== 1 || top() !== dialog || closing(dialog) || !coordinated(dialog)) return;
      if (getComputedStyle(dialog).getPropertyValue('--ap-layer-presentation').trim() !== 'bottom') return;
      const target = event.target;
      if (target.closest('dialog') !== dialog || target.closest('[data-layer-handle], input, textarea, select, [contenteditable="true"], [role="slider"], [data-layer-no-drag]')) return;
      if (!target.closest('[data-layer-surface], .ap-layer__body')) return;
      const touch = event.touches[0];
      const scrollers = [];
      for (let node = target; node && node !== dialog.parentElement; node = node.parentElement) {
        if (node.scrollHeight > node.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(node).overflowY)) scrollers.push(node);
      }
      contentDrag = {x: touch.clientX, y: touch.clientY, id: touch.identifier, scrollers, distance: 0, active: false};
    }, {...options, passive: true});
    dialog.addEventListener('touchmove', event => {
      if (!contentDrag) return;
      if (event.touches.length !== 1 || closing(dialog) || top() !== dialog) {snapBack(); return;}
      const touch = [...event.touches].find(item => item.identifier === contentDrag.id);
      if (!touch) return;
      if (!contentDrag.active && contentDrag.scrollers.some(node => node.scrollTop > 1)) {
        contentDrag.x = touch.clientX; contentDrag.y = touch.clientY;
        return;
      }
      const dx = touch.clientX - contentDrag.x;
      const dy = touch.clientY - contentDrag.y;
      if (!contentDrag.active) {
        if (Math.abs(dx) > 10 && Math.abs(dx) > dy || dy < -10) {contentDrag = null; return;}
        if (dy < 10 || dy < Math.abs(dx) * 1.25) return;
        if (!event.cancelable) {contentDrag = null; return;}
        cancelMovement(dialog);
        dialog.dataset.layerPhase = 'dragging';
        contentDrag.active = true;
      }
      event.preventDefault();
      contentDrag.distance = Math.max(0, dy);
      paintDrag(contentDrag.distance, 'bottom');
    }, {...options, passive: false});
    dialog.addEventListener('touchend', () => {
      if (!contentDrag) return;
      const current = contentDrag;
      if (!current.active) {contentDrag = null; return;}
      suppressClickUntil = performance.now() + 400;
      if (current.distance > dialog.clientHeight / 2) {resetDrag(); close(dialog, 'gesture');}
      else snapBack();
    }, options);
    dialog.addEventListener('touchcancel', () => {if (contentDrag) snapBack();}, options);
    window.addEventListener('resize', () => {if (contentDrag || drag) snapBack();}, options);
    dialog.addEventListener('close', resetDrag, options);
    dialog.addEventListener('click', event => {
      if (performance.now() < suppressClickUntil) {event.preventDefault(); event.stopImmediatePropagation();}
    }, {...options, capture: true});
    dialog.addEventListener('click', event => {if(closing(dialog)){event.preventDefault();event.stopImmediatePropagation();}}, {...options,capture:true});
    dialog.addEventListener('click', event => {
      if (event.target.closest('[data-layer-close]')?.closest('dialog') === dialog) close(dialog, 'button');
    }, options);
  }
  function open(dialog, trigger = document.activeElement) {
    if (!dialog?.showModal || !dialog.isConnected) return false;
    if (dialog.open && awaitingHistory.has(dialog)) {reopenRequests.set(dialog,trigger);return true;}
    const reopening = dialog.open && closing(dialog);
    if (dialog.open && !reopening) return false;
    const from = reopening ? {transform:getComputedStyle(dialog).transform,opacity:getComputedStyle(dialog).opacity} : null;
    if(!reopening)cancelMovement(dialog);
    window.AplovioLazy.mount(dialog.querySelector('.ap-layer__body'));
    manage(dialog);
    const x = window.scrollX, y = window.scrollY;
    if (!stack.length) {
      // Native history restoration must not race the body lock on an instant close.
      if (scrollRestoration?.frame) cancelAnimationFrame(scrollRestoration.frame);
      scrollRestoration ||= {mode:history.scrollRestoration};
      history.scrollRestoration = 'manual';
    }
    if(!reopening)dialog.showModal();
    if (!stack.length) {
      const styles = Object.fromEntries(['position','top','left','width','paddingRight'].map(key => [key, document.body.style[key]]));
      lock = {styles, x, y};
      const gutter = window.innerWidth - document.documentElement.clientWidth;
      Object.assign(document.body.style, {position:'fixed', top:`${-lock.y}px`, left:`${-lock.x}px`, width:'100%', paddingRight:`${parseFloat(getComputedStyle(document.body).paddingRight) + gutter}px`});
    }
    if(!reopening)stack.push({dialog, trigger});
    else {const entry=stack.find(entry=>entry.dialog===dialog);if(entry)entry.trigger=trigger;}
    views.set(dialog,dialog.dataset.layerDefaultView || null);
    if(coordinated(dialog))animate(dialog,'opening',from);
    (focusControl(dialog) || dialog).focus({preventScroll:true});
    emit(dialog, 'open', {trigger,reopening});
    return true;
  }
  function dispose(dialog) {
    close(dialog, 'disconnect', true);
    bindings.get(dialog)?.abort();
    bindings.delete(dialog);
    historyDialogs.delete(dialog.id || dialog.parentElement?.id);
    reopenRequests.delete(dialog); views.delete(dialog);
  }
  const currentSteps = () => history.state?.aplovioLayers?.session === historySession ? history.state.aplovioLayers.steps || history.state.aplovioLayers.ids.map(id=>({id,view:null})) : [];
  function writeHistory(steps,replace = false) {
    const state = {...history.state,aplovioLayers:{session:historySession,ids:[...new Set(steps.map(step=>step.id))],steps}};
    history[replace?'replaceState':'pushState'](state,'',location.href);
    knownHistoryDepth=steps.length;
  }
  function navigate(dialog,view,{replace = false,fromHistory = false,historyDirection = null} = {}) {
    if (!dialog?.open || !dialog.dataset.layerDefaultView || typeof view !== 'string' || view.length > 64 || closing(dialog)) return false;
    if (views.get(dialog) === view) return true;
    if (!fromHistory && ownsHistory(dialog)) {
      const id=dialog.id || dialog.parentElement.id,steps=currentSteps();
      if(replace && steps.at(-1)?.id===id)steps[steps.length-1]={id,view};else steps.push({id,view});
      writeHistory(steps,replace);
    }
    const previous=views.get(dialog);views.set(dialog,view);
    emit(dialog,'view',{view,previous,fromHistory,historyDirection});return true;
  }
  function backView(dialog) {
    if (!dialog?.open || !dialog.dataset.layerDefaultView) return false;
    const id=dialog.id || dialog.parentElement.id,steps=currentSteps();
    let count=0;for(let i=steps.length-1;i>=0 && steps[i].id===id;i--)count++;
    if(ownsHistory(dialog) && count>1){history.go(1-count);return true;}
    return navigate(dialog,dialog.dataset.layerDefaultView,{replace:true});
  }
  function backStep(dialog) {
    const id=dialog?.id || dialog?.parentElement?.id,steps=currentSteps();
    let count=0;for(let i=steps.length-1;i>=0 && steps[i].id===id;i--)count++;
    if(dialog?.open && ownsHistory(dialog) && count>2){history.back();return true;}
    return false;
  }
  function backToView(dialog,view) {
    if(!dialog?.open)return false;
    const id=dialog.id || dialog.parentElement.id,steps=currentSteps();
    const index=steps.findLastIndex(step=>step.id===id && step.view===view);
    if(ownsHistory(dialog) && index>=0 && index<steps.length-1){history.go(index+1-steps.length);return true;}
    return navigate(dialog,view,{replace:true});
  }
  const view = dialog => views.get(dialog) ?? null;
  window.AplovioLayers = {open, close, top, dispose,navigate,backView,backStep,backToView,view,duration,animateContent,disclose,cancelDisclosure};
  class AplovioLayer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (this.dialog) manage(this.dialog);
      const header = this.querySelector('.ap-layer__header');
      if (header) {this.resize = new ResizeObserver(() => this.dialog.style.setProperty('--ap-layer-header-height', `${header.getBoundingClientRect().height}px`)); this.resize.observe(header);}
    }
    disconnectedCallback() { this.resize?.disconnect(); if (this.dialog) dispose(this.dialog); }
    open(trigger) { return open(this.dialog, trigger); }
    close(reason) { return close(this.dialog, reason); }
  }
  customElements.define('aplovio-layer', AplovioLayer);
  document.addEventListener('aplovio:layer-open', event => {
    const dialog = event.target;
    if (!ownsHistory(dialog)) return;
    const id = dialog.id || dialog.parentElement.id;
    historyDialogs.set(id, {dialog, trigger:event.detail.trigger});
    if (historySync) return;
    const steps=currentSteps();
    if(event.detail.reopening && steps.some(step=>step.id===id))return;
    writeHistory([...steps,{id,view:views.get(dialog)}]);
  });
  document.addEventListener('aplovio:layer-beforeclose', event => {
    if (event.defaultPrevented) return;
    if (!ownsHistory(event.target) || historySync || ['history','disconnect'].includes(event.detail.reason)) return;
    if (history.state?.aplovioLayers?.session === historySession) {
      const id=event.target.id || event.target.parentElement.id,steps=currentSteps();
      let count=0;for(let i=steps.length-1;i>=0 && steps[i].id===id;i--)count++;
      if(count){event.preventDefault();awaitingHistory.add(event.target);history.go(-count);}
    }
  });
  window.addEventListener('popstate', event => {
    const state = event.state?.aplovioLayers;
    const desired = state?.session === historySession ? state.ids : [];
    const depth=state?.session === historySession ? (state.steps?.length || state.ids.length) : 0;
    const historyDirection=depth<knownHistoryDepth?'back':'forward';knownHistoryDepth=depth;
    historySync = true;
    for(const entry of historyDialogs.values())awaitingHistory.delete(entry.dialog);
    [...stack].reverse().forEach(({dialog}) => {const id = dialog.id || dialog.parentElement?.id; if (ownsHistory(dialog) && !desired.includes(id)) close(dialog, 'history', true);});
    desired.forEach(id => {
      const entry = historyDialogs.get(id);
      if (!entry?.dialog.isConnected) return;
      if(!entry.dialog.open || closing(entry.dialog))open(entry.dialog, entry.trigger);
      const view=[...(state.steps || [])].reverse().find(step=>step.id===id)?.view || entry.dialog.dataset.layerDefaultView;
      if(view)navigate(entry.dialog,view,{fromHistory:true,historyDirection});
    });
    historySync = false;
    for(const [dialog,trigger] of reopenRequests){reopenRequests.delete(dialog);if(dialog.isConnected)open(dialog,trigger);}
  });
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-layer-open]');
    if (!trigger) return;
    const layer = document.getElementById(trigger.dataset.layerOpen);
    if (layer instanceof AplovioLayer) {event.preventDefault(); layer.open(trigger);}
  });
  document.addEventListener('shopify:section:unload', event => {
    [...stack].reverse().forEach(({dialog}) => {if (event.target.contains(dialog)) dispose(dialog);});
  });
})();
